import React from "react";
import {
  Button,
  Icon,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type IconName,
} from "@salestrak/ui";

import { Box } from "@salestrak/ui";

export { copyText, openHref } from "#app/lib/utils/copy-actions";

/**
 * A hover-revealed action INSIDE a trigger button, where a real button element
 * cannot nest: a click target on a span that stops propagation, so the press
 * never also opens the host. It is a pointer shortcut, not a control: a
 * button's children are presentational, so it takes no role and no tab stop
 * and is hidden from assistive tech (the host's picker holds the keyboard
 * path). The tooltip names it — keep the label to a verb or two ("Clear",
 * "View profile"). `className` carries the host's reveal recipe.
 */
export function InlineTriggerAction({
  icon,
  label,
  onClick,
  className,
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Box
            as="span"
            aria-hidden
            padding="xs"
            rounded="sm"
            cursor="pointer"
            className={className}
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              onClick();
            }}
          >
            <Icon name={icon} size="md" color="muted" />
          </Box>
        }
      />
      <TooltipPopup side="bottom">{label}</TooltipPopup>
    </Tooltip>
  );
}

/**
 * A property row's inline action — the 24px squared ghost rung, labeled for
 * assistive tech and by tooltip. stopPropagation: rows can sit inside
 * pressable surfaces (a preview card, a mobile tap target), so the action
 * press must not also fire the host's click.
 */
export function RowAction({
  icon,
  label,
  onClick,
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            aria-label={label}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              onClick();
            }}
            // Squared: row chrome takes the row's rectangle corners, never
            // the free-standing stadium.
            shape="square"
            size="icon-xs"
            variant="ghost"
          >
            <Icon name={icon} size="sm" />
          </Button>
        }
      />
      <TooltipPopup side="bottom">{label}</TooltipPopup>
    </Tooltip>
  );
}
